import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Calendar, Users, AlertTriangle, TrendingUp, Filter } from 'lucide-react';
import { reportService } from '@/services/reportService';
import { adminService, Hospital } from '@/services/adminService';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { UserLevel } from '@/types/auth.types';
import { MaternalRiskAnalysis } from '@/types/report.types';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

export const MaternalRiskAnalysisReport: React.FC = () => {
  const { utilizator } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MaternalRiskAnalysis | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hospitalId, setHospitalId] = useState('all');
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const { toast } = useToast();

  const isAdmin = utilizator?.user_level_id === UserLevel.ADMINISTRATOR;

  useEffect(() => {
    const loadHospitals = async () => {
      if (isAdmin) {
        try {
          const list = await adminService.listHospitals();
          setHospitals(list);
        } catch (error) {
          console.error('Failed to load hospitals:', error);
        }
      } else if (utilizator?.hospital_id) {
        // For non-admins, lock to their hospital
        setHospitalId(utilizator.hospital_id.toString());
      }
    };
    loadHospitals();
  }, [isAdmin, utilizator]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      // Handle hospital filter
      if (isAdmin) {
        if (hospitalId && hospitalId !== 'all') {
          filters.hospital_id = parseInt(hospitalId);
        }
      } else {
        // Force current hospital for non-admins
        if (utilizator?.hospital_id) {
          filters.hospital_id = utilizator.hospital_id;
        }
      }

      const response = await reportService.getMaternalRiskAnalysis(filters);
      setData(response);
    } catch (error) {
      console.error('Error fetching maternal risk analysis:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele analizei de risc matern',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch once user data is available
    if (utilizator) {
      fetchData();
    }
  }, [utilizator]); // Depend on utilizator to ensure we have the ID for non-admins

  const ageDistributionData = data ? Object.entries(data.age_distribution).map(([range, values]) => ({
    age_range: range,
    count: values.count,
    percentage: values.percentage,
  })) : [];

  const riskFactorsData = data ? Object.entries(data.risk_factors).map(([factor, values]) => ({
    name: factor === 'smoking' ? 'Fumat' :
      factor === 'alcohol' ? 'Alcool' :
        factor === 'diabetes' ? 'Diabet' :
          factor === 'hypertension' ? 'Hipertensiune' : factor,
    value: values.percentage,
    count: values.count,
  })) : [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtre raport
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="start-date">Data început</Label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="end-date">Data sfârșit</Label>
              <input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="hospital">Spital</Label>
              <Select
                value={hospitalId}
                onValueChange={setHospitalId}
                disabled={!isAdmin}
              >
                <SelectTrigger id="hospital">
                  <SelectValue placeholder={isAdmin ? "Toate spitalele" : (utilizator?.hospital_name || "Spitalul curent")} />
                </SelectTrigger>
                <SelectContent>
                  {isAdmin && <SelectItem value="all">Toate spitalele</SelectItem>}
                  {isAdmin ? (
                    hospitals.map(h => (
                      <SelectItem key={h.id} value={h.id.toString()}>
                        {h.name}
                      </SelectItem>
                    ))
                  ) : (
                    utilizator?.hospital_id && (
                      <SelectItem value={utilizator.hospital_id.toString()}>
                        {utilizator.hospital_name || "Spitalul curent"}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={fetchData} className="w-full">
                <Filter className="h-4 w-4 mr-2" />
                Aplică filtre
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <Users className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <p className="text-3xl font-bold text-blue-600">{data.summary.total_patients}</p>
                  <p className="text-sm text-muted-foreground">Total pacienți</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                  <p className="text-3xl font-bold text-orange-600">{data.summary.with_maternal_data}</p>
                  <p className="text-sm text-muted-foreground">Cu date materne</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <TrendingUp className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <p className="text-3xl font-bold text-green-600">{data.summary.completeness_rate.toFixed(1)}%</p>
                  <p className="text-sm text-muted-foreground">Completitudine date</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Distribuție pe grupe de vârstă maternă</CardTitle>
            </CardHeader>
            <CardContent>
              {ageDistributionData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={ageDistributionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="age_range" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#3b82f6" name="Număr pacienți" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Alert>
                  <AlertDescription>
                    Nu există date disponibile pentru distribuția pe vârstă
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Factori de risc identificați</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  {riskFactorsData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={riskFactorsData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value ? value.toFixed(1) : 0}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {riskFactorsData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Alert>
                      <AlertDescription>
                        Nu există date disponibile pentru factorii de risc
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold mb-4">Detalii factori de risc</h4>
                  {riskFactorsData.map((factor, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="font-medium">{factor.name}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{factor.count} cazuri</p>
                        <p className="text-sm text-muted-foreground">{factor.value.toFixed(1)}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Complicații în timpul sarcinii</CardTitle>
            </CardHeader>
            <CardContent>
              {data.pregnancy_complications.length > 0 ? (
                <div className="space-y-2">
                  {data.pregnancy_complications.map((complication, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium">{complication.complication}</span>
                      <div className="text-right">
                        <p className="font-semibold">{complication.count} cazuri</p>
                        <p className="text-sm text-muted-foreground">{complication.percentage.toFixed(1)}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert>
                  <AlertDescription>
                    Nu există date disponibile pentru complicațiile în timpul sarcinii
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
